/**
 * Article table of contents — builds the rail's "In this article" list from the
 * headings the article body section rendered, then keeps the current one marked.
 *
 * Why this runs in the browser rather than in Liquid: the headings live inside
 * `article.content`, one rich-text field owned by the body section, and the
 * links have to point at ids on those headings. Liquid can read the string but
 * cannot write ids back onto markup another snippet outputs, so the two halves
 * — assign the id, and link to it — only meet here.
 *
 * Accessibility: the spy sets `aria-current` and never moves focus. Jumping to
 * a heading is a plain anchor navigation, so the browser does the focus move
 * itself; `.article-toc-anchor` carries the scroll offset that keeps the
 * sticky header from covering the landing target.
 */

/* `[data-article-body-prose]` is what sections/article-body.liquid marks its
   prose column with. The other two are generic fallbacks, tried in order. */
const SECTION_BODY_SELECTORS = ['[data-article-body-prose]', '[data-article-content]', '.rte'];

/* Page-wide, `.rte` is dropped: other sections use it for unrelated rich text. */
const PAGE_BODY_SELECTORS = ['[data-article-body-prose]', '[data-article-content]'];
const DESKTOP_QUERY = 'screen and (min-width: 769px)';
const DEFAULT_HEADER_HEIGHT = 80;

if (!customElements.get('article-toc')) {
  customElements.define(
    'article-toc',
    class ArticleToc extends HTMLElement {
      entries = [];
      frame = 0;
      desktop = null;

      onBreakpointChange = () => {
        // The box is desktop-only in the design, so there is nothing to spy on
        // below the breakpoint.
        if (this.desktop.matches) {
          this.startSpy();
        } else {
          this.stopSpy();
        }
      };

      onScroll = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(this.update);
      };

      update = () => {
        const line = this.headerOffset + 1;
        let active = 0;

        for (const [index, entry] of this.entries.entries()) {
          if (entry.heading.getBoundingClientRect().top <= line) {
            active = index;
          }
        }

        for (const [index, entry] of this.entries.entries()) {
          if (index === active) {
            entry.link.setAttribute('aria-current', 'location');
          } else {
            entry.link.removeAttribute('aria-current');
          }
        }
      };

      connectedCallback() {
        this.build();

        // No headings, no body container, or nothing but empty headings: leave
        // the element hidden rather than showing an empty bordered box.
        if (!this.entries.length) {
          return;
        }

        this.hidden = false;
        this.desktop = window.matchMedia(DESKTOP_QUERY);
        this.desktop.addEventListener('change', this.onBreakpointChange);
        this.onBreakpointChange();
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.frame);
        this.desktop?.removeEventListener('change', this.onBreakpointChange);
        this.stopSpy();
      }

      get list() {
        return this.querySelector('[data-article-toc-list]');
      }

      /* The header measures itself into --header-height on load (see
         sections/header.liquid); 80 matches the fallback the PDP scripts use. */
      get headerOffset() {
        const measured = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10);
        return Number.isNaN(measured) ? DEFAULT_HEADER_HEIGHT : measured;
      }

      /**
       * Locates the element holding the rendered article body.
       * @returns {Element|null}
       */
      findBody() {
        const selector = this.dataset.tocTarget;
        if (selector) {
          return document.querySelector(selector);
        }

        const section = this.closest('.shopify-section');
        const scopes = [
          [section, SECTION_BODY_SELECTORS],
          [document, PAGE_BODY_SELECTORS],
        ];

        for (const [root, selectors] of scopes) {
          if (!root) {
            continue;
          }
          for (const candidate of selectors) {
            const found = root.querySelector(candidate);
            if (found) {
              return found;
            }
          }
        }
        return null;
      }

      build() {
        const HEADING_SELECTOR = this.dataset.selectedTag;
        const body = this.findBody();
        const list = this.list;
        if (!body || !list) {
          return;
        }

        const fragment = document.createDocumentFragment();

        for (const [index, heading] of [...body.querySelectorAll(HEADING_SELECTOR)].entries()) {
          const text = heading.textContent.trim();
          if (!text) {
            continue;
          }

          if (!heading.id) {
            heading.id = this.uniqueId(text, index);
          }
          heading.classList.add('article-toc-anchor');

          const link = document.createElement('a');
          link.className = 'article-toc__link';
          link.href = `#${heading.id}`;
          link.textContent = text.replace(/\bmyth\s*#\d+:\s*/gi, '');

          const item = document.createElement('li');
          item.className = 'article-toc__item';
          item.appendChild(link);
          fragment.appendChild(item);

          this.entries.push({ link, heading });
        }

        list.appendChild(fragment);
      }

      /**
       * @param {string} text - Heading text to slugify
       * @param {number} index - Position in the heading list, for untitled fallbacks
       * @returns {string} An id no other element on the page is using
       */
      uniqueId(text, index) {
        const base =
          text
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '') || `article-section-${index + 1}`;

        let id = base;
        let suffix = 2;
        while (document.getElementById(id)) {
          id = `${base}-${suffix}`;
          suffix += 1;
        }
        return id;
      }

      startSpy() {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.update();
      }

      stopSpy() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
    },
  );
}
